import React, { useCallback, useEffect } from 'react';

import { SingleDatePicker } from './components/SingleDatePicker.component';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';
import { OverallProductivity } from '../DashboardOverviewNew/components/OverallProductivity.component';
import { KeyHighlights } from '../DashboardOverviewNew/components/KeyHighlights.component';
import { ProductivityOverTime } from '../DashboardOverviewNew/components/ProductivityOverTime.component';
import { ActivityBreakdown } from '../DashboardOverviewNew/components/ActivityBreakdown.component';
import { ActivityHeatmap } from '../DashboardOverviewNew/components/ActivityHeatmap.component';
import { TopApplications } from '../DashboardOverviewNew/components/TopApplications.component';
import { FocusVsDistraction } from '../DashboardOverviewNew/components/FocusVsDistraction.component';
import { AIInsights } from '../DashboardOverviewNew/components/AIInsights.component';
import { RecentAlerts } from '../DashboardOverviewNew/components/RecentAlerts.component';
import { TeamPerformance } from '../DashboardOverviewNew/components/TeamPerformance.component';
import { AttendanceSummary } from '../DashboardOverviewNew/components/AttendanceSummary.component';
import { AI_INSIGHTS_DATA } from './data/teamDashboard.data';
import { useTeamDashboardData } from './hooks/useTeamDashboardData';
import styles from '../DashboardOverviewNew/DashboardOverviewNew.module.scss';

export const TeamDashboardComponent = () => {
  const {
    dateRange,
    setDateRange,
    isFetchingStats,
    isFetchingAttendance,
    overallProductivityData,
    keyHighlightsData,
    productivityOverTimeData,
    chartTicks,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    attendanceSummaryData,
    teamPerformanceData,
    heatmapData,
    recentAlertsData,
  } = useTeamDashboardData();

  const setNavbarSlot = useNavbarSlot();

  const handleDateChange = useCallback(
    (dateStr) => setDateRange({ start: dateStr, end: dateStr }),
    [setDateRange]
  );

  useEffect(() => {
    setNavbarSlot(
      <SingleDatePicker
        date={dateRange.start}
        onDateChange={handleDateChange}
      />
    );
    return () => setNavbarSlot(null);
  }, [dateRange.start, handleDateChange, setNavbarSlot]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.row}>
        <OverallProductivity
          data={overallProductivityData}
          isLoading={isFetchingStats}
        />
        <KeyHighlights data={keyHighlightsData} isLoading={isFetchingStats} />
      </div>

      <div className={styles.row2}>
        <ProductivityOverTime
          data={productivityOverTimeData}
          isLoading={isFetchingStats}
          chartTicks={chartTicks}
        />
        <ActivityBreakdown
          data={activityBreakdownData}
          isLoading={isFetchingStats}
        />
        <ActivityHeatmap data={heatmapData} isLoading={isFetchingStats} />
      </div>

      <div className={styles.row3}>
        <TopApplications
          data={topApplicationsData}
          isLoading={isFetchingStats}
        />
        <FocusVsDistraction
          data={focusVsDistractionData}
          isLoading={isFetchingStats}
        />
        <AIInsights data={AI_INSIGHTS_DATA} />
        <RecentAlerts
          data={recentAlertsData ?? []}
          isLoading={isFetchingStats && !recentAlertsData}
        />
      </div>

      <div className={styles.row4}>
        <TeamPerformance data={teamPerformanceData ?? []} />
        <AttendanceSummary
          data={attendanceSummaryData}
          isLoading={isFetchingAttendance}
        />
      </div>
    </div>
  );
};
